// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Client-error beacon — durable capture of a failed turn's error.
 *
 * When a turn fails on the client the red banner shows the error string we
 * already hold; this beacon POSTs that same string (plus the cheap transport
 * context we can read at the catch) to the `/client-error` endpoint so the
 * failure is classifiable server-side (429 / 5xx / 401 / a true transport
 * throw) instead of vanishing into a browser `console.error`. There is no edge
 * access log for the `/invocations` path, so a never-dispatched turn leaves no
 * server record at all — this beacon is the only signal it happened.
 *
 * The beacon is strictly fire-and-forget: it must NEVER throw and NEVER block
 * the caller, because it runs on the failure path where the user is already
 * being shown a banner and nothing here may make that worse. `keepalive: true`
 * lets the request outlive a page/stream teardown; every failure mode (bad
 * config, network reject, non-2xx) is swallowed. `navigator.sendBeacon` is not
 * usable here because it cannot set the Cognito `Authorization` header the
 * endpoint's authorizer requires.
 *
 * The `/client-error` route lives on the same REST API as the feedback
 * endpoint (behind the same Cognito authorizer), so this reuses the published
 * `feedbackApiUrl` base and builds `${base}client-error`, exactly as the
 * sessions/preferences/upload services do.
 */

/** Longest `errMessage` we send — an HTTP-error body can be a large JSON blob;
 * the leading text is what classifies the failure, so a bounded prefix is
 * enough and keeps the DynamoDB item small. */
const MAX_ERR_MESSAGE_LENGTH = 500

/**
 * Collapse an error message into something the WAF will accept, without losing
 * what makes it diagnosable.
 *
 * POR QUÉ EXISTE (medido el 2026-08-18 contra el ambiente desplegado)
 * -------------------------------------------------------------------
 * Cuando CloudFront rechaza un turno, el cuerpo de su 403 es una **página HTML**
 * (`<HTML><HEAD><TITLE>ERROR: The request could not be satisfied</TITLE>…`), y
 * `client.ts` la pone tal cual en `errMessage`. Este beacon la reenviaba entera a
 * `/client-error`, donde el WebACL de CloudFront la inspecciona y la bloquea con
 * `CrossSiteScripting_BODY`: para WAF, un cuerpo con `<HTML><HEAD>` es un intento
 * de XSS. Los logs del WAF tienen los BLOCK en pares — un
 * `SizeRestrictions_BODY` en `/api/chat` y, un instante después, un
 * `CrossSiteScripting_BODY` en `/api/client-error`.
 *
 * O sea que el canal que existe para dejar registro del fallo **fallaba
 * exactamente cuando había un fallo que registrar**, y en silencio: el beacon
 * traga todos los errores por diseño. Seis bloqueos quedaron en los logs del WAF
 * sin una sola contraparte del lado del servidor.
 *
 * Qué se conserva: el título y el encabezado del error, y sobre todo el
 * `Request ID` de CloudFront, que es la llave para correlacionar con sus logs.
 * Qué se tira: las etiquetas. Un mensaje sin `<` ni `>` pasa la regla y sigue
 * clasificando el fallo igual de bien.
 */
export function sanitizeErrMessage(message: string): string {
  const looksLikeHtml = /<\s*(html|!doctype|head|body|h1|h2|title)\b/i.test(message)

  if (looksLikeHtml) {
    const pick = (re: RegExp): string => (re.exec(message)?.[1] ?? "").trim()
    const title = pick(/<title>([\s\S]*?)<\/title>/i)
    const heading = pick(/<h2>([\s\S]*?)<\/h2>/i)
    const requestId = pick(/Request ID:\s*([A-Za-z0-9_\-=]+)/i)

    const parts = [
      "respuesta HTML del edge",
      title && `title="${title}"`,
      heading && heading !== title && `h2="${heading}"`,
      requestId && `cfRequestId=${requestId}`,
    ].filter(Boolean)
    // Se pasa por el mismo saneado final: el título de la página también puede
    // traer signos que la regla mire.
    return stripAngleBrackets(parts.join(" · ")).slice(0, MAX_ERR_MESSAGE_LENGTH)
  }

  return stripAngleBrackets(message).slice(0, MAX_ERR_MESSAGE_LENGTH)
}

/**
 * Quita los signos de menor/mayor y normaliza los espacios.
 *
 * Se aplica también a los mensajes que NO parecen HTML: cualquier error que
 * arrastre un fragmento de markup —un `<div>` en un mensaje de parseo, por
 * ejemplo— dispara la misma regla. Se reemplazan por `‹›` en lugar de borrarlos
 * para que el texto siga leyéndose y no queden palabras pegadas.
 */
function stripAngleBrackets(text: string): string {
  return text
    .replace(/</g, "‹")
    .replace(/>/g, "›")
    .replace(/\s+/g, " ")
    .trim()
}

/** Where in the turn lifecycle the failure happened, inferred from whether any
 * bytes/frames arrived: no frames ⇒ the runtime never flushed (pre-dispatch or
 * a delivered pre-stream status); ≥1 frame ⇒ the stream had started. */
export type ClientErrorPhase = "pre-first-byte" | "mid-stream"

/**
 * One captured client-side turn failure. Every field is either already held at
 * the catch or a cheap synchronous read — capturing must add no heavy plumbing
 * to the turn path.
 */
export interface ClientErrorRecord {
  /** Per-logical-turn id, when the client mints one (reused across retries).
   * Optional: the strands turn body does not carry one today. */
  clientTurnId?: string
  /** The conversation / AgentCore runtime session id for this turn. */
  runtimeSessionId: string
  /** The `X-Amzn-Trace-Id` sent on the fetch — joins to the shim's log line
   * when the turn reached the runtime (and its absence there is the
   * pre-dispatch proof). */
  traceId: string
  /** Where the failure landed relative to the first streamed frame. */
  failurePhase: ClientErrorPhase
  /** HTTP status when the failure was a delivered non-2xx (parsed from the
   * `HTTP <n>:` prefix client.ts mints); absent for a true transport throw. */
  httpStatus?: number
  /** The thrown error's `name` (e.g. "TypeError", "Error"). */
  errName: string
  /** The thrown error's message, truncated to {@link MAX_ERR_MESSAGE_LENGTH}. */
  errMessage: string
  /** How many SSE frames were decoded this turn before it failed (0 ⇒ nothing
   * flushed). Best-effort. */
  framesReceived: number
  /** Milliseconds to the first byte/frame, when one arrived. */
  firstByteMs?: number
  /** Milliseconds of the silent gap that preceded the failure, when known. */
  gapMs?: number
  /** Which attempt this was (for a future bounded retry); absent means first. */
  attempt?: number
  /** `navigator.onLine` at failure time — distinguishes "offline" from a
   * service-side failure. */
  online: boolean
  /** The browser user-agent string, for client-population classification. */
  userAgent: string
}

// Cache the REST API base URL across calls (it never changes within a session);
// same convention as uploadService / preferencesService.
let cachedBaseUrl = ""

/**
 * Read the REST API root from `aws-exports.json` (reused from the feedback API).
 *
 * @returns the API base URL (ending in "/"), or "" when it cannot be resolved
 *   (this service never throws — an unresolvable URL just means no beacon).
 */
async function loadBaseUrl(): Promise<string> {
  if (cachedBaseUrl) return cachedBaseUrl
  const response = await fetch("/aws-exports.json")
  const config = await response.json()
  // The base is optional here on purpose: local-agent mode has no deployed API,
  // and the beacon must degrade to a no-op rather than surface an error.
  cachedBaseUrl = typeof config.feedbackApiUrl === "string" ? config.feedbackApiUrl : ""
  return cachedBaseUrl
}

/**
 * Fire-and-forget POST of a captured turn failure to `/client-error`.
 *
 * Resolves once the request has been dispatched (or once a failure has been
 * swallowed). It never rejects and never throws, so callers can `void` it on
 * the failure path without a try/catch of their own.
 *
 * @param record - the captured failure context.
 * @param idToken - Cognito ID token for the API Gateway authorizer (same token
 *   the feedback/sessions/preferences services use). When absent the beacon is
 *   a no-op — the endpoint is authenticated, so an unauthenticated POST could
 *   not be recorded anyway.
 */
export async function reportClientError(
  record: ClientErrorRecord,
  idToken: string | undefined
): Promise<void> {
  try {
    if (!idToken) return
    const base = await loadBaseUrl()
    if (!base) return

    // Sanear y acotar el mensaje (belt-and-braces con el tope de la propia
    // Lambda): un cuerpo de error enorme infla la petición y el item guardado, y
    // uno con etiquetas HTML hace que el WAF bloquee este mismo POST — ver
    // `sanitizeErrMessage`.
    const payload: ClientErrorRecord = {
      ...record,
      errMessage: sanitizeErrMessage(record.errMessage),
    }

    await fetch(`${base}client-error`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(payload),
      // Survive a page/stream teardown so a failure captured as the tab is torn
      // down still reaches the endpoint.
      keepalive: true,
    })
  } catch {
    // Swallow everything: the beacon must never make a failed turn worse. The
    // failure the beacon exists to capture has already been logged by the
    // caller's own console.error / console.warn breadcrumb.
  }
}
